import React, { useState, useEffect } from 'react';
import { playPhaseStart, playWarning, playTimeUp, playClick, playVictory } from '../sounds';

const CATEGORY_ICONS = {
  'גאוגרפיה': '🌍',
  'מדע': '🔬',
  'ספרות': '📚',
  'ספורט': '⚽',
  'מוזיקה': '🎵',
  'אמנות': '🎨',
  'יהדות': '✡️',
  'היסטוריה': '📖',
  'כללי': '🧩',
  'טבע': '🌿',
};

const OPTION_COLORS = [
  { bg: '#2563EB', hover: '#1D4ED8' },  // blue
  { bg: '#16A34A', hover: '#15803D' },  // green
  { bg: '#EA580C', hover: '#C2410C' },  // orange
  { bg: '#DC2626', hover: '#B91C1C' },  // red
];

const MUTED = '#c4b5d9';

function Timer({ seconds, warning }) {
  return (
    <div className="timer-container">
      <div className={`timer-ring ${warning ? 'warning' : ''}`}>{seconds}</div>
    </div>
  );
}

function Trivia({ gameState, playerId, roomCode, sendMessage, error, clearError }) {
  const {
    phase, currentRound, totalRounds, phaseTimeLeft,
    question, options, category, hasAnswered, myAnswer,
    answeredCount, totalPlayers, correctAnswer,
    playerResults, roundScores, finalScores,
    players, isHost,
  } = gameState;

  const [localTimer, setLocalTimer] = useState(phaseTimeLeft || 0);

  useEffect(() => {
    setLocalTimer(phaseTimeLeft || 0);
  }, [phaseTimeLeft, phase]);

  useEffect(() => {
    if (phase === 'final_results') return;
    const interval = setInterval(() => {
      setLocalTimer(prev => {
        const next = Math.max(0, prev - 1);
        if (next === 5 || next === 3) playWarning();
        if (next === 0 && prev > 0) playTimeUp();
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  useEffect(() => {
    if (phase === 'question') {
      playPhaseStart();
    } else if (phase === 'final_results') {
      playVictory();
    }
  }, [phase, currentRound]);

  const handleAnswer = (index) => {
    if (hasAnswered) return;
    playClick();
    sendMessage('TRIVIA_ANSWER', { answerIndex: index });
  };

  const categoryIcon = CATEGORY_ICONS[category] || '🧩';

  // ===== QUESTION PHASE =====
  if (phase === 'question') {
    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <Timer seconds={localTimer} warning={localTimer <= 5} />

        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
          <p style={{ fontSize: '0.8rem', color: MUTED }}>
            שאלה {currentRound} מתוך {totalRounds}
          </p>
        </div>

        <div style={{
          textAlign: 'center', marginBottom: '16px',
          padding: '6px 16px', borderRadius: '20px',
          background: 'rgba(255,255,255,0.08)', display: 'inline-block',
          alignSelf: 'center', margin: '0 auto 16px auto',
          width: 'fit-content',
        }}>
          <span style={{ fontSize: '1rem' }}>{categoryIcon} {category}</span>
        </div>

        <h2 style={{
          textAlign: 'center', fontSize: '1.3rem', marginBottom: '20px',
          lineHeight: 1.6, fontWeight: 700,
        }}>
          {question}
        </h2>

        {!hasAnswered ? (
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: '10px', marginBottom: '12px',
          }}>
            {options && options.map((opt, i) => (
              <button
                key={i}
                onClick={() => handleAnswer(i)}
                style={{
                  padding: '16px 12px', borderRadius: '14px',
                  background: OPTION_COLORS[i].bg,
                  border: 'none', color: 'white',
                  fontSize: '1rem', fontWeight: 700,
                  fontFamily: 'Heebo, sans-serif',
                  cursor: 'pointer', textAlign: 'center',
                  lineHeight: 1.4, minHeight: '60px',
                  transition: 'transform 0.1s, filter 0.1s',
                }}
                onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.96)'; }}
                onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                {opt}
              </button>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <div style={{
              padding: '16px', borderRadius: '14px',
              background: 'rgba(255,255,255,0.06)',
              border: `2px solid ${OPTION_COLORS[myAnswer]?.bg || '#555'}`,
              marginBottom: '12px',
            }}>
              <p style={{ fontSize: '0.8rem', color: MUTED, marginBottom: '6px' }}>בחרת:</p>
              <p style={{ fontSize: '1.1rem', fontWeight: 700 }}>{options && options[myAnswer]}</p>
            </div>
            <p style={{ color: MUTED, fontSize: '0.9rem' }}>...ממתינים</p>
          </div>
        )}

        <div style={{
          textAlign: 'center', fontSize: '0.8rem', color: MUTED, marginTop: '8px',
        }}>
          {answeredCount} מתוך {totalPlayers} ענו
        </div>

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  // ===== RESULTS PHASE =====
  if (phase === 'results') {
    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <Timer seconds={localTimer} warning={false} />

        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
          <p style={{ fontSize: '0.8rem', color: MUTED }}>
            שאלה {currentRound} מתוך {totalRounds}
          </p>
        </div>

        <div style={{
          textAlign: 'center', marginBottom: '12px',
          padding: '6px 16px', borderRadius: '20px',
          background: 'rgba(255,255,255,0.08)', display: 'inline-block',
          width: 'fit-content', margin: '0 auto 12px auto',
        }}>
          <span style={{ fontSize: '1rem' }}>{categoryIcon} {category}</span>
        </div>

        <h2 style={{
          textAlign: 'center', fontSize: '1.1rem', marginBottom: '16px',
          lineHeight: 1.5, color: MUTED,
        }}>
          {question}
        </h2>

        {/* Options with correct highlighted */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: '8px', marginBottom: '20px',
        }}>
          {options && options.map((opt, i) => {
            const isCorrect = i === correctAnswer;
            return (
              <div
                key={i}
                style={{
                  padding: '14px 12px', borderRadius: '14px',
                  background: isCorrect
                    ? 'rgba(34,197,94,0.25)'
                    : 'rgba(255,255,255,0.04)',
                  border: isCorrect
                    ? '2px solid #22C55E'
                    : '2px solid rgba(255,255,255,0.08)',
                  color: isCorrect ? '#22C55E' : 'rgba(255,255,255,0.35)',
                  fontSize: '0.95rem', fontWeight: isCorrect ? 700 : 500,
                  textAlign: 'center', lineHeight: 1.4,
                }}
              >
                {isCorrect && '✓ '}{opt}
              </div>
            );
          })}
        </div>

        {/* Player results */}
        <div style={{ marginBottom: '12px' }}>
          <h3 style={{ fontSize: '0.9rem', marginBottom: '10px', color: MUTED }}>תוצאות הסיבוב:</h3>
          {playerResults && playerResults.map((pr, i) => (
            <div key={pr.id} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 12px', borderRadius: '10px',
              background: 'rgba(255,255,255,0.04)',
              marginBottom: '6px',
              borderRight: pr.correct ? '3px solid #22C55E' : '3px solid #EF4444',
            }}>
              <span style={{ fontSize: '1.1rem', minWidth: '24px' }}>
                {pr.correct ? '✅' : (pr.answered ? '❌' : '⏱️')}
              </span>
              <span style={{ flex: 1, fontWeight: 600 }}>
                {pr.avatar && <span style={{ marginLeft: '4px' }}>{pr.avatar}</span>}
                {pr.name}
                {pr.id === playerId && <span style={{ color: MUTED, fontSize: '0.75rem' }}> (אני)</span>}
              </span>
              {pr.answered && (
                <span style={{ fontSize: '0.75rem', color: MUTED }}>
                  {pr.timeTaken !== null ? `${pr.timeTaken}s` : ''}
                </span>
              )}
              <span style={{
                fontWeight: 700, minWidth: '40px', textAlign: 'left',
                color: pr.points > 0 ? '#22C55E' : 'rgba(255,255,255,0.3)',
              }}>
                +{pr.points}
              </span>
            </div>
          ))}
        </div>

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  // ===== FINAL RESULTS PHASE =====
  if (phase === 'final_results') {
    const sortedPlayers = finalScores || (players ? [...players].sort((a, b) => b.score - a.score) : []);
    const winner = sortedPlayers[0];

    return (
      <div className="card" style={{ direction: 'rtl' }}>
        {/* Winner announcement */}
        <div style={{
          textAlign: 'center', padding: '24px 16px', marginBottom: '20px',
          background: 'linear-gradient(135deg, rgba(255,215,0,0.15), rgba(255,149,0,0.1))',
          borderRadius: '16px', border: '1px solid rgba(255,215,0,0.3)',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '8px' }}>🏆</div>
          <h2 style={{ fontSize: '1.4rem', marginBottom: '4px', color: '#FFD700' }}>
            {winner?.name} מנצח/ת!
          </h2>
          <p style={{ fontSize: '1.1rem', color: MUTED }}>
            {winner?.score} נקודות
          </p>
        </div>

        {/* Final scoreboard */}
        <div className="result-section" style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '12px' }}>טבלת ניקוד סופית</h3>
          <div className="leaderboard">
            {sortedPlayers.map((p, i) => (
              <div key={p.id} className="leaderboard-item">
                <span className="leaderboard-rank">
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`}
                </span>
                <span className="leaderboard-name">
                  {p.avatar && <span style={{ marginLeft: '4px' }}>{p.avatar}</span>}
                  {p.name}
                  {p.id === playerId && <span className="me-badge"> (אני)</span>}
                </span>
                <span className="leaderboard-score">{p.score}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Host controls */}
        {isHost && (
          <>
            <button
              className="btn btn-primary"
              onClick={() => { clearError(); sendMessage('NEXT_ROUND', {}); }}
              style={{
                width: '100%', marginBottom: '8px',
                background: '#FF9500', borderColor: '#FF9500',
              }}
            >
              סיבוב חדש! 🔄
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => { clearError(); sendMessage('NEW_GAME', {}); }}
              style={{ width: '100%' }}
            >
              חזרה ללובי
            </button>
          </>
        )}
        {!isHost && <p className="waiting">ממתינים שהמנהל ימשיך...</p>}

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  return <div className="card"><p style={{ textAlign: 'center', color: MUTED }}>טוען...</p></div>;
}

export default Trivia;
